/**
 * Section categories (PP-1) — the one home for what the builder knows about
 * section categories as a SEQUENCE: their curated order, how a section is
 * named to a person, what the sentence "between Hero and Services" is, and
 * which category a page most likely wants next.
 *
 * Pure and import-free, like shared/section-order.js: the section list, the
 * shell, the glass toolbar, the picker and their tests all read from here, so
 * none of them can drift into its own copy of the ladder or the order.
 */

/** Curated display order — common website-building flow top-to-bottom. */
export const CATEGORY_ORDER = Object.freeze([
  'navigation',
  'hero',
  'special-codestitches',
  'shopify',
  'services',
  'side-by-side',
  'why-choose-us',
  'stats',
  'steps',
  'gallery',
  'reviews',
  'meet-the-team',
  'quotes',
  'pricing',
  'faq',
  'cta',
  'forms-contact',
  'footer'
]);

/**
 * Categories a page never "wants next": shared slots (nav/footer are set once,
 * not inserted), Shopify blocks, and the special-codestitches grab bag.
 */
export const NEXT_CATEGORY_SKIP = Object.freeze([
  'navigation',
  'footer',
  'shopify',
  'special-codestitches'
]);

/**
 * Is this the parser's "could not classify" marker? (SS-1a)
 *
 * Promoted from four identical private copies (section-list, add-section-modal,
 * property-panel, section-manager) — plus two that had DRIFTED to a coercing
 * `String(category || '') === 'unknown'` variant, which disagreed with these on
 * category-less sections. One home ends the drift.
 */
export function isUnknownCategory(category) {
  return typeof category !== 'string' || category.trim().toLowerCase() === 'unknown';
}

/**
 * The two sides of "whose sections am I looking at?", as one option list.
 *
 * Add Section and the variant picker both ask it, in the same corner of their
 * header, with the same segmented control. Two surfaces answering one question
 * in two vocabularies ("All templates" on one, "All sources" on the other — the
 * shape this replaced) teaches someone to read one and not the other, and the
 * wording is the half most likely to drift, because each surface's copy reads
 * fine in isolation.
 *
 * The values are the contract: `codestitch` is the catalog alone, `all` widens
 * to every ingested template. Callers own their own `ariaLabel`, because the
 * NOUN differs — Add Section scopes sections, the picker scopes variants.
 */
export const SECTION_SOURCE_OPTIONS = Object.freeze([
  Object.freeze({ value: 'codestitch', label: 'CodeStitch' }),
  Object.freeze({ value: 'all', label: 'All sources' })
]);

/**
 * The CodeStitch handle for a record — `stitch-2153`, or '' when it has none.
 *
 * ONE function because three surfaces show this identity and each had, or was
 * about to grow, its own two-step version of it: the Add Section card, the
 * variant picker's caption, and the section list's fact line. The empty string
 * is the whole contract — a template-derived record has no stitch handle, and
 * every caller renders nothing rather than a fallback. Its `catalogKey`
 * (`template-lawngoblins.com-nav-0031`) is not a substitute: it is long enough
 * to crush the name beside it and it is not the handle anyone quotes into a
 * search box or a bug report.
 *
 * Reads `stitchNumber` first and `catalogKey` second on purpose. The number is
 * the usual source but not the only one — a CodeStitch catalog key already IS
 * `stitch-NNNN`, which is what a placed section in project state carries, and a
 * fixture (or any record missing the field) would otherwise silently lose its
 * handle.
 */
export function stitchHandle(record) {
  if (!record || typeof record !== 'object') return '';
  const number = record.stitchNumber;
  if (typeof number === 'number' && Number.isFinite(number)) return `stitch-${number}`;
  const key = typeof record.catalogKey === 'string' ? record.catalogKey : '';
  return key.startsWith('stitch-') ? key : '';
}

/**
 * Should a template-origin section render its captured per-section
 * screenshot? (SS-1a promoted it; SS-2 removed the unknown-category guard:
 * an unclassified section KEEPS its picture. `unknown` is a statement about
 * the classifier, not about the screenshot — and the rows that most need
 * identifying must not be the only ones without one.)
 *
 * `int-hero` stays withheld: interior pages are often normalized to that
 * generic section id even when the captured screenshot uses the source DOM id
 * (banner-*, hero-*, etc.), so its shot is genuinely wrong, not merely
 * unlabelled.
 */
export function shouldUseTemplateSectionThumbnail(section) {
  if (!section) return false;
  return section.templateSectionId !== 'int-hero';
}

/**
 * The shared display-name ladder with its provenance (SS-1b).
 *
 * Order: freeform → user rename → category-map display name → a name DERIVED
 * from the section's own content (its first heading) → the raw category —
 * but never the literal parser marker `unknown`, which is a classification
 * outcome, not a name → 'Section'.
 *
 * `source` is what lets a surface render a derived name differently (quoted,
 * lighter) from one a person typed: a derived name is evidence, not a label
 * someone chose, and must read as such.
 *
 * @returns {{name: string, source: 'freeform'|'renamed'|'category'|'derived'|'fallback'}}
 */
export function resolveSectionName(section, categoryMap = {}, { derivedName } = {}) {
  if (!section) return { name: 'Section', source: 'fallback' };
  if (section.type === 'freeform') return { name: 'Blank Section', source: 'freeform' };
  if (section.displayName) return { name: section.displayName, source: 'renamed' };
  const mapped = categoryMap?.[section.category]?.displayName;
  if (mapped) return { name: mapped, source: 'category' };
  if (derivedName) return { name: derivedName, source: 'derived' };
  if (section.category && !isUnknownCategory(section.category)) {
    return { name: titleCaseCategory(section.category), source: 'category' };
  }
  return { name: 'Section', source: 'fallback' };
}

/**
 * A category id as words: `why-choose-us` → "Why Choose Us". The rung below
 * the category map, so a section reads the same name whether or not the map
 * has loaded for the surface asking (BC-14). The map still wins where it says
 * something the id cannot ("FAQ", "Call to Action").
 */
export function titleCaseCategory(category) {
  return String(category || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * The shared display-name ladder — moved here from shared/version-diff.js
 * (which re-exports it), so confirm-dialog labels, the section list, diff
 * summaries, the picker's context line and its tiles all name a section
 * identically. Since SS-1b a thin wrapper over `resolveSectionName`; the one
 * behaviour change is the `unknown` rung (a section whose category is the
 * parser marker no longer prints the literal word). Since BC-14 any other raw
 * category is title-cased rather than printed verbatim, so a surface without
 * the map says "Hero", never "hero". Surfaces with no map of their own call
 * `formatSectionName` (shared/section-display-name.js), which reads the cached
 * one.
 *
 * @param {object} section
 * @param {object} [categoryMap] — data/category-map.json shape:
 *   {[category]: {displayName, ...}}
 * @param {object} [opts] — {derivedName} forwarded to resolveSectionName
 */
export function sectionDisplayName(section, categoryMap = {}, opts) {
  return resolveSectionName(section, categoryMap, opts).name;
}

/**
 * Where a new section will land, as a sentence for a dialog's context line.
 *
 * `position` is the insert index. Anything that is not a non-negative integer
 * within the page (null, NaN, a float, a negative, past the end) means "at
 * the end" — the same coercion `pasteSectionAt` applies, so the sentence and
 * the insert can never disagree.
 *
 * @param {Array<object>} sections — the page's sections
 * @param {number|null|undefined} position
 * @param {object} [categoryMap]
 * @returns {string}
 */
export function describeInsertPosition(sections, position, categoryMap = {}) {
  const list = Array.isArray(sections) ? sections : [];
  if (list.length === 0) return 'as the first section';
  const at =
    Number.isInteger(position) && position >= 0 && position < list.length ? position : null;
  if (at === null) return 'at the end of the page';
  const name = (s) => sectionDisplayName(s, categoryMap);
  if (at === 0) return `above ${name(list[0])}`;
  return `between ${name(list[at - 1])} and ${name(list[at])}`;
}

/**
 * The category a page most likely wants after `neighbourCategory` — the
 * picker's default tab when opened from a "+" marker. Nobody inserts a second
 * hero under a hero; the page's own flow says what comes next.
 *
 * Rules, in order:
 *  1. Scan CATEGORY_ORDER after the neighbour (from the top when the neighbour
 *     is unknown or is itself a skipped category).
 *  2. First category not in NEXT_CATEGORY_SKIP and not already on the page.
 *  3. Else the first non-skipped category after the start, even if the page
 *     already has one (a repeat is allowed).
 *  4. Else the first non-skipped entry of CATEGORY_ORDER.
 * The result is NEVER a NEXT_CATEGORY_SKIP entry.
 *
 * @param {string|null|undefined} neighbourCategory
 * @param {Array<object|string>} [pageSections] — sections, or bare category strings
 * @returns {string}
 */
export function nextCategoryAfter(neighbourCategory, pageSections = []) {
  const present = new Set(
    (Array.isArray(pageSections) ? pageSections : [])
      .map((s) => (typeof s === 'string' ? s : s?.category))
      .filter(Boolean)
  );
  const skip = new Set(NEXT_CATEGORY_SKIP);
  const candidates = CATEGORY_ORDER.filter((cat) => !skip.has(cat));
  const neighbourIdx =
    typeof neighbourCategory === 'string' && !skip.has(neighbourCategory)
      ? CATEGORY_ORDER.indexOf(neighbourCategory)
      : -1;
  const after = CATEGORY_ORDER.slice(neighbourIdx + 1).filter((cat) => !skip.has(cat));
  return after.find((cat) => !present.has(cat)) || after[0] || candidates[0];
}
